/**
 * Phone entry for GCC guests.
 *
 * The widget's job is to ACCEPT what a guest actually types and normalise it,
 * not to demand strict E.164 from someone on a phone keyboard. Rejecting
 * `055 512 345` outright loses a booking; normalising it does not.
 *
 * This mirrors `normalisePhone` in the API's guestMatch.ts deliberately, so the
 * key the widget shows a guest and the key the server dedupes on are the same
 * one. A test pins the two against each other's cases.
 */

export const GCC_COUNTRIES = [
  { code: '974', label: 'Qatar', flag: 'QA', nationalLength: [8] },
  { code: '971', label: 'UAE', flag: 'AE', nationalLength: [9] },
  { code: '966', label: 'Saudi Arabia', flag: 'SA', nationalLength: [9] },
  { code: '965', label: 'Kuwait', flag: 'KW', nationalLength: [8] },
  { code: '973', label: 'Bahrain', flag: 'BH', nationalLength: [8] },
  { code: '968', label: 'Oman', flag: 'OM', nationalLength: [8] },
] as const;

export type CountryCode = (typeof GCC_COUNTRIES)[number]['code'];

/** Qatar, because that is where the venues are. */
export const DEFAULT_COUNTRY: CountryCode = '974';

const ARABIC_INDIC = '٠١٢٣٤٥٦٧٨٩';
const EASTERN_ARABIC = '۰۱۲۳۴۵۶۷۸۹';

/**
 * Fold Arabic-Indic and Persian digits to 0-9.
 *
 * A guest typing on an Arabic keyboard produces ٠٥٥٥١٢٣٤٥. Without this the
 * number fails validation and the guest is told their own phone number is
 * invalid, which is where they close the tab.
 */
export function foldDigits(input: string): string {
  let out = '';
  for (const ch of input) {
    const ai = ARABIC_INDIC.indexOf(ch);
    if (ai >= 0) {
      out += String(ai);
      continue;
    }
    const ea = EASTERN_ARABIC.indexOf(ch);
    if (ea >= 0) {
      out += String(ea);
      continue;
    }
    out += ch;
  }
  return out;
}

/**
 * Reduce a typed number to digits: country code plus national number.
 *
 * Returns null when it cannot plausibly be a phone number, so the caller can
 * show a message rather than submitting something the server will reject.
 */
export function normalisePhone(
  input: string | null | undefined,
  defaultCountry: CountryCode = DEFAULT_COUNTRY,
): string | null {
  if (!input) return null;

  let digits = foldDigits(input).replace(/\D/g, '');
  if (digits.length === 0) return null;

  if (digits.startsWith('00')) digits = digits.slice(2);

  for (const country of GCC_COUNTRIES) {
    if (
      digits.startsWith(country.code) &&
      (country.nationalLength as readonly number[]).includes(digits.length - country.code.length)
    ) {
      return digits;
    }
  }

  // A trunk zero: 055512345 -> 55512345.
  if (digits.startsWith('0')) {
    const trimmed = digits.replace(/^0+/, '');
    const lengths = GCC_COUNTRIES.find((c) => c.code === defaultCountry)?.nationalLength ?? [];
    if ((lengths as readonly number[]).includes(trimmed.length)) return defaultCountry + trimmed;
    digits = trimmed;
  }

  const lengths = GCC_COUNTRIES.find((c) => c.code === defaultCountry)?.nationalLength ?? [];
  if ((lengths as readonly number[]).includes(digits.length)) return defaultCountry + digits;

  // A country we do not model, but a sane international length.
  if (digits.length >= 10 && digits.length <= 15) return digits;

  return null;
}

/** E.164, for display and for sending. */
export function toE164(input: string, defaultCountry: CountryCode = DEFAULT_COUNTRY): string | null {
  const normalised = normalisePhone(input, defaultCountry);
  return normalised ? `+${normalised}` : null;
}

export function isValidPhone(
  input: string,
  defaultCountry: CountryCode = DEFAULT_COUNTRY,
): boolean {
  return normalisePhone(input, defaultCountry) !== null;
}

/**
 * Group the national part for readability: `5551 2345`.
 *
 * Formatting as the guest types is deliberately NOT done - a field that
 * rewrites itself under the cursor is maddening on a phone, and the value is
 * normalised on submit anyway.
 */
export function formatForDisplay(
  input: string,
  defaultCountry: CountryCode = DEFAULT_COUNTRY,
): string {
  const normalised = normalisePhone(input, defaultCountry);
  if (!normalised) return input;

  const country = GCC_COUNTRIES.find((c) => normalised.startsWith(c.code));
  if (!country) return `+${normalised}`;

  const national = normalised.slice(country.code.length);
  const grouped =
    national.length === 8
      ? `${national.slice(0, 4)} ${national.slice(4)}`
      : `${national.slice(0, 2)} ${national.slice(2, 5)} ${national.slice(5)}`;

  return `+${country.code} ${grouped}`;
}
